import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Configure axios base URL
const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';
axios.defaults.baseURL = API_BASE_URL;

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);                 // Account-level user: { id, username, isAdmin }
  const [characters, setCharacters] = useState([]);        // Character list for selection screen
  const [selectedCharacter, setSelectedCharacter] = useState(null); // Full character data when playing
  const [loading, setLoading] = useState(true);

  // On mount: restore session from stored tokens
  useEffect(() => {
    const charToken = localStorage.getItem('token');
    const acctToken = localStorage.getItem('accountToken');

    if (charToken) {
      // Character was selected — verify the character-level token
      axios.defaults.headers.common['Authorization'] = `Bearer ${charToken}`;
      verifyCharacterToken(acctToken);
    } else if (acctToken) {
      // Logged in but no character selected — verify account-level token
      axios.defaults.headers.common['Authorization'] = `Bearer ${acctToken}`;
      verifyAccountToken();
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyCharacterToken = async (acctToken) => {
    try {
      const response = await axios.get('/api/auth/verify');
      if (response.data.hasCharacter) {
        const d = response.data.user;
        setUser({ id: d.user_id, username: d.username, isAdmin: d.isAdmin || d.is_admin || false });
        setSelectedCharacter(d);
      } else {
        // Token was actually account-level — show character select
        setUser(response.data.user);
        setCharacters(response.data.characters || []);
      }
    } catch (error) {
      console.error('Character token verification failed:', error);
      // Try falling back to account token
      if (acctToken) {
        localStorage.removeItem('token');
        axios.defaults.headers.common['Authorization'] = `Bearer ${acctToken}`;
        verifyAccountToken();
        return;
      }
      logout();
    } finally {
      setLoading(false);
    }
  };

  const verifyAccountToken = async () => {
    try {
      const response = await axios.get('/api/auth/verify');
      setUser(response.data.user);
      setCharacters(response.data.characters || []);
    } catch (error) {
      console.error('Account token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      const { token: acctToken, user: userData, characters: charList } = response.data;

      localStorage.setItem('accountToken', acctToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${acctToken}`;
      setUser(userData);
      setCharacters(charList || []);
      setSelectedCharacter(null);

      return { success: true, characters: charList };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const selectCharacter = async (characterId) => {
    try {
      // Use account token for this request
      const acctToken = localStorage.getItem('accountToken');
      const response = await axios.post('/api/auth/select-character',
        { characterId },
        { headers: { 'Authorization': `Bearer ${acctToken}` } }
      );

      const { token: charToken, character } = response.data;
      localStorage.setItem('token', charToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${charToken}`;
      setSelectedCharacter(character);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to select character' };
    }
  };

  const switchCharacter = useCallback(() => {
    // Clear character-level token but keep account token
    localStorage.removeItem('token');
    const acctToken = localStorage.getItem('accountToken');
    if (acctToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${acctToken}`;
    }
    setSelectedCharacter(null);
    // Refresh character list
    fetchCharacters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCharacters = async () => {
    try {
      const response = await axios.get('/api/auth/characters');
      setCharacters(response.data.characters || []);
    } catch (error) {
      console.error('Failed to fetch characters:', error);
    }
  };

  const createCharacter = async (characterData) => {
    try {
      // Use account token
      const acctToken = localStorage.getItem('accountToken');
      const response = await axios.post('/api/auth/create-character',
        { character: characterData },
        { headers: { 'Authorization': `Bearer ${acctToken}` } }
      );
      // Refresh the character list
      await fetchCharacters();
      return { success: true, character: response.data.character };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to create character' };
    }
  };

  const deleteCharacter = async (characterId) => {
    try {
      const acctToken = localStorage.getItem('accountToken');
      await axios.delete(`/api/auth/character/${characterId}`,
        { headers: { 'Authorization': `Bearer ${acctToken}` } }
      );
      await fetchCharacters();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to delete character' };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post('/api/auth/register', userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Registration failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('accountToken');
    setUser(null);
    setSelectedCharacter(null);
    setCharacters([]);
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    characters,
    selectedCharacter,
    login,
    register,
    selectCharacter,
    switchCharacter,
    createCharacter,
    deleteCharacter,
    fetchCharacters,
    logout,
    loading,
    isAuthenticated: !!user,
    hasCharacter: !!selectedCharacter
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
